import { Navigate, Link } from "react-router-dom";
import { CalendarCheck, LogOut, Snowflake, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ServiceHistory } from "@/components/ServiceHistory";
import { MyRequests } from "@/components/MyRequests";
import { AdminServiceRecords } from "@/components/AdminServiceRecords";
import { AdminLeads } from "@/components/AdminLeads";
import { AdminEstimates } from "@/components/AdminEstimates";
import { AdminCommercialPlans } from "@/components/AdminCommercialPlans";
import { AdminPayments } from "@/components/AdminPayments";
import { AdminDigestButton } from "@/components/AdminDigestButton";
import { AdminVisitReports } from "@/components/AdminVisitReports";
import { AdminTechnicians } from "@/components/AdminTechnicians";
import { AdminCustomers } from "@/components/AdminCustomers";
import { TechnicianPortal } from "@/components/tech/TechnicianPortal";
import { ChangePassword } from "@/components/ChangePassword";
import { PLAN_LABELS } from "@/components/CustomerEditor";
import { BOOKING_URL } from "@/data/site";
import { Seo } from "@/components/Seo";

const ProfileCard = ({ user }) => (
  <div data-testid="account-profile-card" className="bg-white border border-zinc-200 p-6 rounded-sm">
    <h2 className="font-heading font-bold text-lg text-brand-ink mb-4">Your Profile</h2>
    <p className="text-sm text-zinc-700"><span className="text-zinc-500">Name:</span> {user.name}</p>
    <p className="text-sm text-zinc-700 mt-1"><span className="text-zinc-500">Email:</span> {user.email}</p>
    {user.phone && <p className="text-sm text-zinc-700 mt-1"><span className="text-zinc-500">Phone:</span> {user.phone}</p>}
    <ChangePassword />
  </div>
);

const PlanCard = ({ user }) => {
  const hasPlan = user.plan_status && user.plan_status !== "none";
  return (
    <div data-testid="account-plan-card" className="bg-brand text-white p-6 rounded-sm">
      <Snowflake className="w-7 h-7 mb-3" />
      <h2 className="font-heading font-bold text-lg">Maintenance Plan</h2>
      {hasPlan ? (
        <div className="mt-2">
          <p data-testid="account-plan-status" className="font-bold text-brand-ice">{PLAN_LABELS[user.plan_status]}</p>
          {user.plan_start && <p className="text-blue-100 text-sm mt-1">Started: {user.plan_start}</p>}
          {user.plan_renewal && <p className="text-blue-100 text-sm mt-0.5">Renews: {user.plan_renewal}</p>}
        </div>
      ) : (
        <p className="text-blue-100 text-sm mt-2 leading-relaxed">
          You're not on a maintenance plan yet. Two tune-ups a year keeps your AC efficient and your warranty protected.
        </p>
      )}
      <Link to="/maintenance-plans" data-testid="account-plan-link" className="inline-block mt-4 bg-brand-ice text-brand-ink font-bold px-5 py-2.5 rounded-sm text-sm hover:bg-[#7FA8F0] transition-colors duration-200">
        View Plan Details
      </Link>
    </div>
  );
};

const TechnicianView = ({ user, logout }) => (
  <div data-testid="technician-portal" className="max-w-xl mx-auto px-4 py-8 sm:py-12">
    <div className="flex items-center justify-between gap-4 mb-6">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Technician Portal</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink">Hi, {user.name.split(" ")[0]}</h1>
      </div>
      <button onClick={logout} data-testid="account-logout-btn"
        className="border border-zinc-300 hover:border-brand-red hover:text-brand-red text-zinc-600 font-medium px-4 py-2.5 rounded-sm flex items-center gap-2 transition-colors duration-200 text-sm">
        <LogOut className="w-4 h-4" /> Sign Out
      </button>
    </div>
    <TechnicianPortal />
    <div className="bg-white border border-zinc-200 rounded-sm p-4 mt-6">
      <ChangePassword />
    </div>
  </div>
);

export default function Account() {
  const { user, logout } = useAuth();

  if (user === false) return <Navigate to="/login" replace />;
  if (user === null) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;
  }

  const isAdmin = user.role === "admin";

  if (user.role === "technician") {
    return (
      <div data-testid="account-page" className="bg-brand-subtle min-h-[70vh]">
        <Seo title="Technician Portal | Central Air Depot" description="File a service visit report." noindex />
        <TechnicianView user={user} logout={logout} />
      </div>
    );
  }

  return (
    <div data-testid="account-page" className="bg-brand-subtle min-h-[70vh]">
      <Seo title="My Account | Central Air Depot" description="Manage your Central Air Depot service history, maintenance plan and requests." noindex />
      <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Manage My AC</p>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-black text-brand-ink">Hi, {user.name.split(" ")[0]}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && <AdminDigestButton />}
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="account-book-btn"
              className="bg-brand hover:bg-brand-dark text-white font-bold px-5 py-3 rounded-sm flex items-center gap-2 transition-colors duration-200">
              <CalendarCheck className="w-4 h-4" /> Book Service
            </a>
            <button onClick={logout} data-testid="account-logout-btn"
              className="border border-zinc-300 hover:border-brand-red hover:text-brand-red text-zinc-600 font-medium px-5 py-3 rounded-sm flex items-center gap-2 transition-colors duration-200">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <ProfileCard user={user} />
            {!isAdmin && <PlanCard user={user} />}
          </div>

          <div className="lg:col-span-2">
            <ServiceHistory />
            {!isAdmin && <MyRequests />}
            {isAdmin && <AdminVisitReports />}
            {isAdmin && <AdminTechnicians />}
            {isAdmin && <AdminCustomers />}
            {isAdmin && <AdminServiceRecords />}
            {isAdmin && <AdminLeads />}
            {isAdmin && <AdminEstimates />}
            {isAdmin && <AdminCommercialPlans />}
            {isAdmin && <AdminPayments />}
          </div>
        </div>
      </div>
    </div>
  );
}
